import React, { useEffect, useState } from "react";
import useGet from "../../../services/hooks/useGet";
import { apiEndpoints } from "../../../shared/constants/api-constant";
import usePost from "../../../services/hooks/usePost";
import { useDispatch, useSelector } from "react-redux";
import { CommonFunctions } from "../../../shared/utils/CommonFunctions";
import { hideLoader, showLoader } from "../../../store/currentPreferencesSlice";
import NotesInputRow, {
  INote,
} from "../../../shared/ReusableComponents/NotesInputRow";
import NotesViewUpdateRow from "../../../shared/ReusableComponents/NotesViewUpdateRow";
import usePut from "../../../services/hooks/usePut";
import useDelete from "../../../services/hooks/useDelete";
import { Stack } from "@mui/material";
import ConfirmDialog from "../../../shared/ReusableComponents/ConfirmDialog";
import { ICONS } from "../../../shared/constants/enums";

function ProgressNotes({ parentId }: { parentId: number }) {
  const { data, getService, loading } = useGet();
  const { putService } = usePut();
  const { deleteService } = useDelete();
  const [notes, setNotes] = useState<INote[]>([]);
  const { postService, loading: postLoading } = usePost();
  const dispatch = useDispatch();
  const auth = useSelector((state: any) => state.login);
  const [openDialog, setOpenDialog] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<number | null>(null);
  const [currentEditingNoteId, setCurrentEditingNoteId] = useState<
    number | null
  >(null);

  const currentUser = auth.userInfo;
  const isCurrentUserStudent = CommonFunctions.IsCurrentUserStudent();
  const isCurrentSuperUser = CommonFunctions.IsCurrentSuperUser();
  const isCurrentSubCoordinator = CommonFunctions.IsCurrentUserSubCoordinator();
  const isCurrentTutor = CommonFunctions.IsCurrentUserTutor();
  const isCurrentUserStudentOrTutor = isCurrentUserStudent || isCurrentTutor;

  const onAdd = (text: string, offsetInMinutes: number) => {
    if (!text) return;
    dispatch(showLoader());

    const newNote = isCurrentUserStudentOrTutor
      ? {
          caseId: parentId,
          notesText: text,
          userId: currentUser?.id,
          roleId: currentUser?.roleId,
        }
      : {
          offset: offsetInMinutes,
          templateId: parentId,
          notesText: text,
        };
    postService(
      isCurrentUserStudentOrTutor
        ? apiEndpoints.NOTE
        : apiEndpoints.TEMPLATE_NOTE,
      newNote,
      "New Progress Note has been Successfully Added."
    ).then(() => {
      getService(
        isCurrentUserStudentOrTutor
          ? `${apiEndpoints.NOTES}/${parentId}`
          : `${apiEndpoints.TEMPLATE_NOTES}/${parentId}`
      ).then(() => {
        dispatch(hideLoader());
      });
    });
  };

  useEffect(() => {
    dispatch(showLoader());
    !data &&
      getService(
        isCurrentUserStudentOrTutor
          ? `${apiEndpoints.NOTES}/${parentId}`
          : `${apiEndpoints.TEMPLATE_NOTES}/${parentId}`
      ).then(() => {
        dispatch(hideLoader());
      });
  }, [parentId]);

  useEffect(() => {
    if (data) {
      setNotes(data);
    }
  }, [data]);

  const handleUpdateNote = async (
    id: number,
    text: string,
    offsetInMinutes: number
  ) => {
    dispatch(showLoader());

    const updatedNote = {
      id,
      notesText: text,
      offset: offsetInMinutes,
    };
    await putService(
      `${apiEndpoints.TEMPLATE_NOTE}`,
      updatedNote,
      "Progress Note has been Successfully Updated."
    )
      .then(() => getService(`${apiEndpoints.TEMPLATE_NOTES}/${parentId}`))
      .then((updatedData: any) => {
        setNotes(updatedData);
        dispatch(hideLoader());
      });
  };

  const handleEditNote = (id: number | null) => {
    setCurrentEditingNoteId(id);
  };

  const confirmDeleteNote = (noteId: number) => {
    setNoteToDelete(noteId);
    setOpenDialog(true);
  };

  const handleDeleteNote = () => {
    if (noteToDelete !== null) {
      dispatch(showLoader());
      deleteService(
        `${apiEndpoints.TEMPLATE_NOTE}/${noteToDelete}`,
        {},
        "Progress Note has been Successfully Deleted."
      )
        .then(() => getService(`${apiEndpoints.TEMPLATE_NOTES}/${parentId}`))
        .then((updatedData: any) => {
          setNotes(updatedData);
          dispatch(hideLoader());
        });
    }
    setOpenDialog(false);
    setNoteToDelete(null);
  };

  useEffect(() => {
    if (Array.isArray(data)) {
      setNotes(data);
    } else {
      setNotes([]);
    }
  }, [data]);

  return (
    <React.Fragment>
      {Array.isArray(notes) && notes.length > 0 ? (
        notes.map((note) => (
          <NotesViewUpdateRow
            key={note.id}
            handleUpdateNote={handleUpdateNote}
            handleDeleteNote={() => confirmDeleteNote(note.id)}
            isCurrentUserStudentOrTutor={isCurrentUserStudentOrTutor}
            note={note}
            currentEditingNoteId={currentEditingNoteId}
            handleEditNote={handleEditNote}
            userRole={isCurrentSuperUser}
          />
        ))
      ) : isCurrentSubCoordinator ? (
        <Stack
          alignItems="center"
          justifyContent="center"
          fontSize="1rem"
          color={"var(--color-border-model)"}
        >
          No data available.
        </Stack>
      ) : null}

      <NotesInputRow
        isCurrentUserStudentOrTutor={isCurrentUserStudentOrTutor}
        onAdd={onAdd}
        note={undefined}
        isCurrentSuperUser={isCurrentSuperUser}
      />
      <ConfirmDialog
        dialogOpen={openDialog}
        setDialogOpen={() => setOpenDialog(false)}
        handleConfirm={() => handleDeleteNote()}
        text={"Are you sure you want to Delete this Progress Note?"}
        width="xs"
        confirmText="Delete"
        cancelText="Cancel"
        icon={ICONS.DELETE}
        className="delete-icon"
      />
    </React.Fragment>
  );
}

export default ProgressNotes;
